import { useEffect, useRef, useState } from 'react';
import compassUrl from '../assets/compass.svg';
import { View } from '../CommonTypes';

import {
  currentSlideAtom,
  showAvailableState,
  showCompassOptionsAtom,
  showFirstTimePopupAtom,
} from '../states/planner';
import { useAtom } from 'jotai';

const compassToDegrees: Record<string, number> = {
  N: 180,
  NNE: 157.5,
  NE: 135,
  ENE: 112.5,
  E: 90,
  ESE: 67.5,
  SE: 45,
  SSE: 22.5,
  S: 0,
  SSW: 337.5,
  SW: 315,
  WSW: 292.5,
  W: 270,
  WNW: 247.5,
  NW: 225,
  NNW: 202.5,
};

export const CompassButton = ({ siteViews, overviewPicture }: { siteViews: View[]; overviewPicture: string }) => {
  const [showCompassOptions, setShowCompassOptions] = useAtom(showCompassOptionsAtom);
  const [currentSlide] = useAtom(currentSlideAtom);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [, setFirstTimePopup] = useAtom(showFirstTimePopupAtom);
  const [, setShowAvailable] = useAtom(showAvailableState);

  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    let timer: NodeJS.Timeout | null = null;
    let timer2: NodeJS.Timeout | null = null;
    let pulseTimer: NodeJS.Timeout | null = null;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setFirstTimePopup(true);
          setShowAvailable(true);

          setPulse(true);
          pulseTimer = setTimeout(() => setPulse(false), 5000);

          timer = setTimeout(() => {
            setFirstTimePopup(false);
          }, 4850);

          timer2 = setTimeout(() => {
            setShowAvailable(false);
          }, 5000);

          // stop observing after first trigger
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
      if (timer2) clearTimeout(timer2);
      if (pulseTimer) clearTimeout(pulseTimer);
    };
  }, [containerRef]);

  const directions = siteViews.map(view => view.compass);

  if (!overviewPicture)
    return (
      <div className="absolute left-1/2 h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 transition-all sm:h-[82px] sm:w-[82px]">
        <img
          src={compassUrl}
          alt="compass"
          style={{
            transform: `rotate(${compassToDegrees[directions[currentSlide]]}deg) scale(1.03)`,
            transition: 'transform 0.5s ease-out',
          }}
          className="pointer-events-none object-contain"
        />
      </div>
    );

  return (
    <div ref={containerRef}>
      {/* Compass button */}
      <button
        data-tooltip-id="viewSelector"
        data-tooltip-place="top"
        onClick={() => setShowCompassOptions(!showCompassOptions)}
        className={`compass absolute left-1/2 top-1/2 flex h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-[2px_3px_8px_rgba(0,0,0,0.4)] transition-all hover:ring-4 hover:ring-black/10 sm:h-[82px] sm:w-[82px] ${showCompassOptions ? 'scale-95 shadow-none' : ''}`}
      >
        <div className={`${pulse ? 'animate-pulse-scale' : ''} flex items-center justify-center`}>
          <img
            src={compassUrl}
            alt="compass"
            style={{
              transform: `rotate(${compassToDegrees[directions[currentSlide]]}deg) scale(1.03)`,
              transition: 'transform 0.5s ease-out',
            }}
            className="pointer-events-none object-contain"
          />
        </div>
      </button>
    </div>
  );
};
